/**
 * Suggest packing operations from a trip's route DAG: start, one after each event (no dest storage), then "Unpack at" for HQ.
 */

import type { PathSegment, RouteStructure } from './routeInference';

export interface SuggestedOperation {
    sourceEventIds: string[];
    targetEventIds: string[];
    sourceStorageLocationIds: string[];
    targetStorageLocationIds: string[];
    label: string;
    /** Trailer (item type) IDs involved in this operation, for display. */
    trailerIds: string[];
}

export interface SuggestOptions {
    sourceStorageLocationId: string;
    destStorageLocationId: string;
    eventIdsInOrder: string[];
    /** Optional: human-readable names for "After {eventName}" and "Return to {destName}" labels */
    eventNames?: Map<string, string> | Record<string, string>;
    destName?: string;
}

/**
 * Returns 1 + M suggestions: start (at source storage), then one "after event" per event that appears on at least one path (last = return to dest).
 * Events in eventIdsInOrder that do not appear on any path are skipped.
 */
export function suggestPackingOperations(routeStructure: RouteStructure, options: SuggestOptions): SuggestedOperation[] {
    const { sourceStorageLocationId, destStorageLocationId, eventIdsInOrder, eventNames, destName = 'HQ' } = options;

    const getEventName = (id: string) => {
        if (eventNames instanceof Map) return eventNames.get(id) ?? id;
        if (eventNames && typeof eventNames === 'object') return (eventNames as Record<string, string>)[id] ?? id;
        return id;
    };

    const result: SuggestedOperation[] = [];

    if (routeStructure.trailerPaths.size === 0) return result;

    // Step 1: First event set (first event segment on each path, after any leading storage)
    const firstEventIds = new Set<string>();
    for (const path of Array.from(routeStructure.trailerPaths.values())) {
        const firstEventSeg = path.segments.find((s: PathSegment): s is PathSegment & { kind: 'event' } => s.kind === 'event');
        if (firstEventSeg && firstEventSeg.kind === 'event') firstEventIds.add(firstEventSeg.eventId);
    }

    // Trailers that have first events on their path (for start)
    const startTrailerIds: string[] = [];
    for (const [tid, path] of Array.from(routeStructure.trailerPaths.entries())) {
        if (path.segments.some((s: PathSegment) => s.kind === 'event' && firstEventIds.has((s as { eventId: string }).eventId))) {
            startTrailerIds.push(tid);
        }
    }

    // Step 2: Start suggestion
    const firstEventIdsArr: string[] = [];
    for (const id of Array.from(firstEventIds)) firstEventIdsArr.push(id);
    result.push({
        sourceStorageLocationIds: [sourceStorageLocationId],
        sourceEventIds: [],
        targetEventIds: firstEventIdsArr,
        targetStorageLocationIds: [],
        label: 'Start trip',
        trailerIds: startTrailerIds,
    });

    // Step 3: "After event" suggestions (do NOT include dest storage — that becomes "Unpack at" later)
    const lastEventIdsWithDestNext = new Set<string>(); // events whose only next is dest storage (for "Unpack at")
    const unpackTrailerIds: string[] = [];

    for (let i = 0; i < eventIdsInOrder.length; i++) {
        const eventId = eventIdsInOrder[i];

        // Skip if event not on any path
        let onAnyPath = false;
        const nextEventIds = new Set<string>();
        let hasNextDestStorage = false;
        const laterEventIds = new Set<string>();
        const trailerIdsForThisEvent: string[] = [];

        for (const path of Array.from(routeStructure.trailerPaths.values())) {
            const idx = path.segments.findIndex((s: PathSegment) => s.kind === 'event' && s.eventId === eventId);
            if (idx === -1) continue;
            onAnyPath = true;
            trailerIdsForThisEvent.push(path.trailerId);

            const nextSeg = path.segments[idx + 1];
            if (nextSeg) {
                if (nextSeg.kind === 'event') nextEventIds.add(nextSeg.eventId);
                else if (nextSeg.kind === 'storage' && nextSeg.storageLocationId === destStorageLocationId) {
                    hasNextDestStorage = true;
                    lastEventIdsWithDestNext.add(eventId);
                    unpackTrailerIds.push(path.trailerId);
                }

                // AFP = downstream events that are NOT the next destination
                for (let j = idx + 2; j < path.segments.length; j++) {
                    const seg = path.segments[j];
                    if (seg.kind === 'event') laterEventIds.add(seg.eventId);
                }
            }
        }

        if (!onAnyPath) continue;

        // Skip "after last event" when next is only dest storage — we'll add "Unpack at" instead
        if (hasNextDestStorage && nextEventIds.size === 0) continue;

        const nextEventIdsArr: string[] = [];
        for (const id of Array.from(nextEventIds)) nextEventIdsArr.push(id);
        const trailerIdsSet = new Set(trailerIdsForThisEvent);
        const trailerIdsArr: string[] = [];
        for (const id of Array.from(trailerIdsSet)) trailerIdsArr.push(id);
        result.push({
            sourceEventIds: [eventId],
            sourceStorageLocationIds: [],
            targetEventIds: nextEventIdsArr,
            targetStorageLocationIds: [], // never include dest in post-event ops
            label: `After ${getEventName(eventId)}`,
            trailerIds: trailerIdsArr,
        });
    }

    // Step 4: "Unpack at <destName>" — items transferred to dest storage; sources = events connected to dest in DAG
    if (lastEventIdsWithDestNext.size > 0) {
        const lastEventIdsArr: string[] = [];
        for (const id of Array.from(lastEventIdsWithDestNext)) lastEventIdsArr.push(id);
        const unpackTrailerIdsSet = new Set(unpackTrailerIds);
        const unpackTrailerIdsArr: string[] = [];
        for (const id of Array.from(unpackTrailerIdsSet)) unpackTrailerIdsArr.push(id);
        result.push({
            sourceEventIds: lastEventIdsArr,
            sourceStorageLocationIds: [],
            targetEventIds: [],
            targetStorageLocationIds: [destStorageLocationId],
            label: `Unpack at ${destName}`,
            trailerIds: unpackTrailerIdsArr,
        });
    }

    return result;
}
